/**
 * <dialed-gift-guard data-kit-product="123" data-gift-product="456">
 *
 * Keeps the free gift (the crate) in step with the kit after every cart change:
 * - no kit in the cart → remove the gift line(s)
 * - more than one gift → keep one (the discount applies once per order)
 *
 * Rendered by snippets/cart-drawer.liquid (and the cart page). The discount itself is the
 * automatic "Free crate with your pre-order" Buy X get Y in admin.
 */
import { StandardEvents } from '@shopify/events';
import { fetchCart, setLineQuantity } from '@theme/dialed-cart';

class DialedGiftGuard extends HTMLElement {
  #busy = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;
        const items = detail?.items ?? (await fetchCart()).items;
        await this.#check(items);
      })
      .catch(() => {});
  };

  /** @param {Array<{ key: string, product_id: number, quantity: number }>} items */
  async #check(items) {
    if (this.#busy || !items) return;
    const kitId = Number(this.dataset.kitProduct);
    const giftId = Number(this.dataset.giftProduct);
    if (!kitId || !giftId) return;

    const kits = items.filter((i) => i.product_id === kitId).reduce((n, i) => n + i.quantity, 0);
    const gifts = items.filter((i) => i.product_id === giftId);
    const giftQty = gifts.reduce((n, i) => n + i.quantity, 0);

    /** @type {Array<[string, number]>} */
    const changes = [];
    if (kits === 0) {
      for (const g of gifts) changes.push([g.key, 0]);
    } else if (giftQty > 1) {
      gifts.forEach((g, i) => changes.push([g.key, i === 0 ? 1 : 0]));
    }
    if (!changes.length) return;

    this.#busy = true;
    try {
      for (const [key, quantity] of changes) await setLineQuantity(this, key, quantity);
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('dialed-gift-guard')) customElements.define('dialed-gift-guard', DialedGiftGuard);
